import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PlusCircle, Image as ImageIcon, MapPin, Target, Calendar, Loader2, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { powerCreate, POWER_CATEGORIES } from "@/lib/powerApi";

const COVER_PRESETS = [
  "https://images.unsplash.com/photo-1541872703-74c5e44368f9?w=1200",
  "https://images.unsplash.com/photo-1494599948593-3dafe8338d71?w=1200",
  "https://images.unsplash.com/photo-1523419409543-a5e549c1faa8?w=1200",
  "https://images.unsplash.com/photo-1552053831-71594a27632d?w=1200",
  "https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?w=1200",
  "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=1200",
];

export default function PowerCreate() {
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [form, setForm] = useState({
    title: "",
    summary: "",
    story: "",
    category: POWER_CATEGORIES[0],
    goal: 1000,
    location: "",
    cover_url: COVER_PRESETS[0],
    closes_in_days: 60,
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.type === "number" ? Number(e.target.value) : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { nav("/login?next=/power/create"); return; }
    if (form.title.trim().length < 6) { toast.error("Give your petition a title (6+ chars)"); return; }
    if (form.story.trim().length < 20) { toast.error("Add a story so people know why it matters (20+ chars)"); return; }
    setBusy(true);
    try {
      const r = await powerCreate(form);
      toast.success("Petition published — start sharing!");
      nav(`/power/p/${r.petition.slug}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't publish");
    } finally { setBusy(false); }
  };

  if (loading) return null;

  return (
    <div data-testid="power-create" className="max-w-2xl mx-auto pb-6">
      <div className="rounded-3xl bg-gradient-to-br from-[#1E88FF] to-[#0A1A2F] text-white p-6 sm:p-8 mb-6">
        <div className="inline-flex items-center gap-1 bg-white/15 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3">
          <Sparkles className="w-3 h-3" /> Every big change starts with one voice
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight leading-tight">Start your petition</h1>
        <p className="text-white/80 text-sm mt-2 max-w-md">Write a title. Tell your story. Set a goal. We'll help you share it far and wide.</p>
      </div>

      {!user && (
        <div className="mb-4 p-4 rounded-2xl bg-white border border-[#E4EAF3] text-sm text-[#0A1A2F]/80">
          <span className="font-semibold text-[#0A1A2F]">Heads up — </span>
          you'll need to <a href="/login?next=/power/create" className="text-[#1E88FF] font-semibold">sign in</a> to publish. Your draft below will still be here.
        </div>
      )}

      <form onSubmit={submit} className="space-y-5 bg-white rounded-3xl border border-[#E4EAF3] p-5 sm:p-6 shadow-sm">
        <Field label="Petition title" hint="Short, clear, and specific — this is what people see first.">
          <input data-testid="create-title" value={form.title} onChange={set("title")} required
            placeholder="e.g. Protect our neighborhood park from development"
            className="w-full px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:border-[#1E88FF] focus:outline-none text-[#0A1A2F] font-semibold" />
        </Field>

        <Field label="One-line summary" hint="Optional — shown on cards & search.">
          <input data-testid="create-summary" value={form.summary} onChange={set("summary")}
            maxLength={280}
            placeholder="Sum it up in a single sentence"
            className="w-full px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:border-[#1E88FF] focus:outline-none" />
        </Field>

        <Field label="Your story" hint="Explain what's at stake, why it matters, and what change you're asking for.">
          <textarea data-testid="create-story" value={form.story} onChange={set("story")} required
            rows={8} minLength={20} maxLength={8000}
            placeholder="Tell your story…"
            className="w-full px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:border-[#1E88FF] focus:outline-none leading-relaxed" />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Category">
            <select data-testid="create-category" value={form.category} onChange={set("category")}
              className="w-full px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:border-[#1E88FF] focus:outline-none">
              {POWER_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Signature goal" icon={<Target className="w-4 h-4" />}>
            <input data-testid="create-goal" type="number" min={25} max={10_000_000}
              value={form.goal} onChange={set("goal")}
              className="w-full px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:border-[#1E88FF] focus:outline-none" />
          </Field>
          <Field label="Location" icon={<MapPin className="w-4 h-4" />}>
            <input data-testid="create-location" value={form.location} onChange={set("location")}
              placeholder="City, state, or country"
              className="w-full px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:border-[#1E88FF] focus:outline-none" />
          </Field>
          <Field label="Days to run" icon={<Calendar className="w-4 h-4" />}>
            <input data-testid="create-days" type="number" min={1} max={365}
              value={form.closes_in_days} onChange={set("closes_in_days")}
              className="w-full px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:border-[#1E88FF] focus:outline-none" />
          </Field>
        </div>

        <Field label="Cover image" icon={<ImageIcon className="w-4 h-4" />} hint="Pick a preset or paste any image URL.">
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
            {COVER_PRESETS.map((url) => (
              <button key={url} type="button" data-testid={`cover-preset-${COVER_PRESETS.indexOf(url)}`}
                onClick={() => setForm((f) => ({ ...f, cover_url: url }))}
                className={"aspect-video rounded-lg overflow-hidden border-2 transition-all " + (form.cover_url === url ? "border-[#1E88FF] ring-2 ring-[#1E88FF]/30" : "border-transparent hover:border-[#E4EAF3]")}>
                <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
          <input data-testid="create-cover-url" value={form.cover_url} onChange={set("cover_url")}
            placeholder="https://…"
            className="w-full px-4 py-2 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:border-[#1E88FF] focus:outline-none text-xs" />
        </Field>

        <button type="submit" data-testid="create-submit" disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#1E88FF] to-[#0A1A2F] text-white font-black py-4 rounded-full shadow-lg hover:shadow-xl transition-shadow disabled:opacity-60">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <PlusCircle className="w-5 h-5" />}
          Publish petition
        </button>
      </form>
    </div>
  );
}

function Field({ label, hint, icon, children }) {
  return (
    <label className="block">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-[#0A1A2F]/60 mb-1.5">
        {icon}{label}
      </div>
      {children}
      {hint && <div className="text-[11px] text-[#0A1A2F]/50 mt-1">{hint}</div>}
    </label>
  );
}
